import { prisma } from './prisma';
import { PRStatus, PRItemStatus, POStatus, GRStatus, InventoryTxType } from '@prisma/client';
import type { PRRecord, PRAuditRecord } from './mockPrStore';
import type { PORecord } from './mockPoStore';
import type { GoodsReceiptRecord } from './mockGrStore';
import type { ItemRecord } from './mockItemStore';
import type { SupplierRecord } from './mockSupplierStore';
import type { UomRecord } from './mockUomStore';
import type { WarehouseRecord } from './mockWarehouseStore';

let isHydrated = false;

/**
 * Hydrates all in-memory operational stores directly from MariaDB tables.
 * This ensures the application reads from MariaDB upon server startup.
 */
export async function hydrateFromMariaDB(stores: {
  prDatabase?: PRRecord[];
  poDatabase?: PORecord[];
  grDatabase?: GoodsReceiptRecord[];
  costLayers?: any[];
  inventoryTxs?: any[];
  itemDatabase?: ItemRecord[];
  masterItems?: any[];
  supplierDatabase?: SupplierRecord[];
  supplierMaster?: any[];
  uomDatabase?: UomRecord[];
  warehouseDatabase?: WarehouseRecord[];
  auditLogs?: PRAuditRecord[];
}) {
  try {
    // 1. UoMs
    if (stores.uomDatabase) {
      const uoms = await prisma.unitOfMeasure.findMany({ orderBy: { code: 'asc' } });
      if (uoms.length > 0) {
        stores.uomDatabase.length = 0;
        for (const u of uoms) {
          stores.uomDatabase.push({
            id: u.id,
            code: u.code,
            name: u.name,
            symbol: u.symbol,
            description: '',
            category: 'General',
            createdAt: u.createdAt.toISOString(),
          });
        }
      }
    }

    // 2. Items
    if (stores.itemDatabase) {
      const items = await prisma.item.findMany({
        include: { defaultUom: true },
        orderBy: { code: 'asc' },
      });
      if (items.length > 0) {
        stores.itemDatabase.length = 0;
        if (stores.masterItems) stores.masterItems.length = 0;
        for (const it of items) {
          const mapped: ItemRecord = {
            id: it.id,
            code: it.code,
            name: it.name,
            category: it.category,
            specs: it.specs || '',
            defaultUomId: it.defaultUomId,
            uomCode: it.defaultUom.code,
            defaultPrice: 10000,
            status: it.isActive ? 'ACTIVE' : 'INACTIVE',
            description: '',
            minStockLevel: 0,
            createdAt: it.createdAt.toISOString(),
            updatedAt: it.updatedAt.toISOString(),
          };
          stores.itemDatabase.push(mapped);
          if (stores.masterItems) {
            stores.masterItems.push({
              id: mapped.id,
              code: mapped.code,
              name: mapped.name,
              category: mapped.category,
              specs: mapped.specs,
              defaultUomId: mapped.defaultUomId,
              uomCode: mapped.uomCode,
              defaultPrice: mapped.defaultPrice,
            });
          }
        }
      }
    }

    // 3. Suppliers
    if (stores.supplierDatabase) {
      const sups = await prisma.supplier.findMany({
        include: { bankAccounts: true },
        orderBy: { code: 'asc' },
      });
      if (sups.length > 0) {
        stores.supplierDatabase.length = 0;
        if (stores.supplierMaster) stores.supplierMaster.length = 0;
        for (const s of sups) {
          const mapped: SupplierRecord = {
            id: s.id,
            code: s.code,
            name: s.name,
            phone: s.phone || '',
            email: s.email || '',
            address: s.address || '',
            paymentTerms: s.paymentTerms || 'NET 30',
            status: s.isActive ? 'ACTIVE' : 'INACTIVE',
            bankAccounts: s.bankAccounts.map((b) => ({
              bankName: b.bankName,
              accountNumber: b.accountNumber,
              accountHolderName: b.accountHolderName,
              isPrimary: b.isPrimary,
            })),
            createdAt: s.createdAt.toISOString(),
            updatedAt: s.updatedAt.toISOString(),
          };
          stores.supplierDatabase.push(mapped);
          if (stores.supplierMaster) {
            stores.supplierMaster.push({
              id: s.id,
              code: mapped.code,
              name: mapped.name,
              phone: mapped.phone,
              email: mapped.email,
              address: mapped.address,
              paymentTerms: mapped.paymentTerms,
              bankAccounts: mapped.bankAccounts,
            });
          }
        }
      }
    }

    // 4. Warehouses
    if (stores.warehouseDatabase) {
      const whs = await prisma.warehouse.findMany({ orderBy: { code: 'asc' } });
      if (whs.length > 0) {
        stores.warehouseDatabase.length = 0;
        for (const w of whs) {
          stores.warehouseDatabase.push({
            id: w.id,
            code: w.code,
            name: w.name,
            type: w.type as any,
            address: w.address || '',
            picName: w.picName || '',
            picPhone: w.picPhone || '',
            capacity: w.capacity || 0,
            capacityUnit: w.capacityUnit || 'Pallet',
            status: w.isActive ? 'ACTIVE' : 'INACTIVE',
            notes: w.notes || '',
            createdAt: w.createdAt.toISOString(),
            updatedAt: w.updatedAt.toISOString(),
          });
        }
      }
    }

    // 5. Purchase Requests
    if (stores.prDatabase) {
      const prs = await prisma.purchaseRequest.findMany({
        include: {
          requester: true,
          items: {
            include: { item: true, uom: true },
          },
        },
        orderBy: { createdAt: 'desc' },
      });
      if (prs.length > 0) {
        stores.prDatabase.length = 0;
        for (const p of prs) {
          stores.prDatabase.push({
            id: p.id,
            prNumber: p.prNumber,
            title: p.title || 'Pengadaan Material',
            date: p.date.toISOString(),
            requesterId: p.requesterId,
            requesterName: p.requester.name,
            department: p.department || 'Procurement',
            notes: p.notes || undefined,
            status: p.status as any,
            isRevision: p.isRevision,
            revisionNotes: p.revisionNotes || undefined,
            createdAt: p.createdAt.toISOString(),
            updatedAt: p.updatedAt.toISOString(),
            items: p.items.map((it) => ({
              id: it.id,
              prId: it.prId,
              itemId: it.itemId,
              itemCode: it.item.code,
              itemName: it.item.name,
              category: it.item.category,
              requestedQty: Number(it.requestedQty),
              approvedQty: Number(it.approvedQty),
              allocatedQty: Number(it.allocatedQty),
              uomId: it.uomId,
              uomCode: it.uom.code,
              estimatedPrice: 10000,
              status: it.status as any,
              notes: it.notes || undefined,
            })),
          });
        }
      }
    }

    // 6. Purchase Orders
    if (stores.poDatabase) {
      const pos = await prisma.purchaseOrder.findMany({
        include: {
          supplier: true,
          purchaser: true,
          items: {
            include: { item: true, uom: true, prItem: { include: { pr: true } } },
          },
        },
        orderBy: { createdAt: 'desc' },
      });
      if (pos.length > 0) {
        stores.poDatabase.length = 0;
        for (const p of pos) {
          stores.poDatabase.push({
            id: p.id,
            poNumber: p.poNumber,
            date: p.date.toISOString(),
            supplierId: p.supplierId,
            supplierSnapshot: (p.supplierSnapshot as any) || {
              id: p.supplier.id,
              code: p.supplier.code,
              name: p.supplier.name,
              phone: p.supplier.phone,
              email: p.supplier.email,
              address: p.supplier.address,
              paymentTerms: p.supplier.paymentTerms,
            },
            bankAccountSnapshot: (p.bankAccountSnapshot as any) || {},
            purchaserId: p.purchaserId,
            purchaserName: p.purchaser.name,
            status: p.status as any,
            isRevision: p.isRevision,
            revisionNotes: p.revisionNotes || undefined,
            subtotal: Number(p.subtotal),
            tax: Number(p.tax),
            total: Number(p.total),
            notes: p.notes || undefined,
            createdAt: p.createdAt.toISOString(),
            updatedAt: p.updatedAt.toISOString(),
            items: p.items.map((it) => ({
              id: it.id,
              poId: it.poId,
              prItemId: it.prItemId || '',
              prNumber: it.prItem?.pr?.prNumber || '',
              itemId: it.itemId,
              itemCode: it.item.code,
              itemName: it.item.name,
              orderedQty: Number(it.orderedQty),
              receivedQty: Number(it.receivedQty),
              initialOrderedQty: it.initialOrderedQty ? Number(it.initialOrderedQty) : Number(it.orderedQty),
              releasedQty: Number(it.releasedQty),
              releaseReason: it.releaseReason || undefined,
              unitPrice: Number(it.unitPrice),
              subtotal: Number(it.subtotal),
              uomId: it.uomId,
              uomCode: it.uom.code,
              notes: it.notes || undefined,
            })),
          });
        }
      }
    }

    // 7. Goods Receipts
    if (stores.grDatabase) {
      const grs = await prisma.goodsReceipt.findMany({
        include: {
          po: { include: { supplier: true } },
          receiver: true,
          items: {
            include: { item: true, uom: true, poItem: true },
          },
        },
        orderBy: { createdAt: 'desc' },
      });
      if (grs.length > 0) {
        stores.grDatabase.length = 0;
        for (const g of grs) {
          const totalGood = g.items.reduce((acc, it) => acc + Number(it.receivedQty), 0);
          const totalDamaged = g.items.reduce((acc, it) => acc + Number(it.rejectedQty), 0);
          stores.grDatabase.push({
            id: g.id,
            grNumber: g.grNumber,
            vendorDeliveryNote: g.vendorDeliveryNote,
            receiveDate: g.receiveDate.toISOString(),
            poId: g.poId,
            poNumber: g.po.poNumber,
            supplierId: g.po.supplierId,
            supplierName: g.po.supplier.name,
            receiverId: g.receiverId,
            receiverName: g.receiver.name,
            status: g.status as any,
            notes: g.notes || undefined,
            createdAt: g.createdAt.toISOString(),
            updatedAt: g.updatedAt.toISOString(),
            totalGoodQty: totalGood,
            totalRejectedQty: totalDamaged,
            items: g.items.map((it) => ({
              id: it.id,
              grId: it.grId,
              poItemId: it.poItemId,
              itemId: it.itemId,
              itemCode: it.item.code,
              itemName: it.item.name,
              receivedQty: Number(it.receivedQty),
              rejectedQty: Number(it.rejectedQty),
              uomId: it.uomId,
              uomCode: it.uom.code,
              unitCost: Number(it.poItem.unitPrice),
              conditionNotes: it.conditionNotes || undefined,
              costLayerId: `layer-${it.id}`,
              inventoryTxId: `tx-${it.id}`,
              createdAt: it.createdAt.toISOString(),
            })),
          });
        }
      }
    }

    // 8. Cost Layers & Transactions
    if (stores.costLayers) {
      const layers = await prisma.inventoryCostLayer.findMany({
        include: { item: { include: { defaultUom: true } }, grItem: { include: { gr: true } } },
      });
      if (layers.length > 0) {
        stores.costLayers.length = 0;
        for (const l of layers) {
          stores.costLayers.push({
            id: l.id,
            grItemId: l.grItemId,
            itemId: l.itemId,
            itemCode: l.item.code,
            itemName: l.item.name,
            uomCode: l.item.defaultUom.code,
            initialQty: Number(l.initialQty),
            remainingQty: Number(l.remainingQty),
            unitCost: Number(l.unitCost),
            createdAt: l.createdAt.toISOString(),
          });
        }
      }
    }

    if (stores.inventoryTxs) {
      const txs = await prisma.inventoryTransaction.findMany({
        include: { item: { include: { defaultUom: true } }, gr: true },
      });
      if (txs.length > 0) {
        stores.inventoryTxs.length = 0;
        for (const t of txs) {
          stores.inventoryTxs.push({
            id: t.id,
            grId: t.grId || '',
            itemId: t.itemId,
            itemCode: t.item.code,
            itemName: t.item.name,
            quantity: Number(t.quantity),
            uomCode: t.item.defaultUom.code,
            txType: t.txType,
            txDate: t.txDate.toISOString(),
            createdAt: t.createdAt.toISOString(),
          });
        }
      }
    }

    // 9. Audit Logs
    if (stores.auditLogs) {
      const logs = await prisma.auditLog.findMany({
        include: { actor: true },
        orderBy: { timestamp: 'desc' },
        take: 1000,
      });
      if (logs.length > 0) {
        stores.auditLogs.length = 0;
        for (const a of logs) {
          stores.auditLogs.push({
            id: a.id,
            entityType: a.entityType,
            entityId: a.entityId,
            fromStatus: a.fromStatus || undefined,
            toStatus: a.toStatus || undefined,
            actorId: a.actorId || undefined,
            actorName: a.actor?.name || 'System',
            actorRole: 'ADMIN',
            action: a.action,
            reason: a.reason || undefined,
            metadata: (a.metadata as any) || undefined,
            timestamp: a.timestamp.toISOString(),
          });
        }
      }
    }

    isHydrated = true;
    console.log('[dbBridge] Successfully hydrated all application state directly from MariaDB');
  } catch (err) {
    console.error('[dbBridge] Error during hydration from MariaDB:', err);
  }
}

// ─── DIRECT PERSISTENCE FUNCTIONS TO MARIADB ──────────────────────────────────

export async function syncPRToDatabase(pr: PRRecord) {
  if (process.env.NODE_ENV === 'test' || process.env.SKIP_DB_SYNC === 'true') {
    return;
  }
  try {
    let requesterId = pr.requesterId;
    const user = await prisma.user.findUnique({ where: { id: requesterId } });
    if (!user) requesterId = 'usr-purchaser-01';

    let prRec = await prisma.purchaseRequest.findFirst({
      where: { OR: [{ id: pr.id }, { prNumber: pr.prNumber }] },
    });

    if (prRec) {
      prRec = await prisma.purchaseRequest.update({
        where: { id: prRec.id },
        data: {
          prNumber: pr.prNumber,
          title: pr.title || 'Pengadaan Material',
          department: pr.department || 'Procurement',
          status: pr.status as PRStatus,
          isRevision: pr.isRevision || false,
          revisionNotes: pr.revisionNotes || null,
          notes: pr.notes || null,
          date: new Date(pr.date || Date.now()),
        },
      });
    } else {
      prRec = await prisma.purchaseRequest.create({
        data: {
          id: pr.id,
          prNumber: pr.prNumber,
          title: pr.title || 'Pengadaan Material',
          department: pr.department || 'Procurement',
          status: pr.status as PRStatus,
          isRevision: pr.isRevision || false,
          revisionNotes: pr.revisionNotes || null,
          notes: pr.notes || null,
          date: new Date(pr.date || Date.now()),
          requesterId,
        },
      });
    }

    for (const item of pr.items) {
      let uom = await prisma.unitOfMeasure.findFirst({
        where: { OR: [{ id: item.uomId }, { code: item.uomCode }] },
      });
      if (!uom) uom = await prisma.unitOfMeasure.findFirst({ where: { code: 'PCS' } });

      let dbItem = await prisma.item.findFirst({
        where: { OR: [{ id: item.itemId }, { code: item.itemCode }] },
      });
      if (!dbItem) continue;

      const itStatus = (item.status === 'APPROVED' ? 'APPROVED' : item.status === 'REJECTED' ? 'REJECTED' : 'PENDING') as PRItemStatus;

      await prisma.purchaseRequestItem.upsert({
        where: { id: item.id },
        update: {
          requestedQty: item.requestedQty,
          approvedQty: item.approvedQty || 0,
          allocatedQty: item.allocatedQty || 0,
          status: itStatus,
          notes: item.notes || item.rejectionReason || null,
        },
        create: {
          id: item.id,
          prId: prRec.id,
          itemId: dbItem.id,
          uomId: uom!.id,
          requestedQty: item.requestedQty,
          approvedQty: item.approvedQty || 0,
          allocatedQty: item.allocatedQty || 0,
          status: itStatus,
          notes: item.notes || item.rejectionReason || null,
        },
      });
    }
  } catch (err) {
    console.error('[dbBridge] Error syncing PR to MariaDB:', err);
  }
}

export async function syncPOToDatabase(po: PORecord) {
  if (process.env.NODE_ENV === 'test' || process.env.SKIP_DB_SYNC === 'true') {
    return;
  }
  try {
    let sup = await prisma.supplier.findFirst({
      where: { OR: [{ id: po.supplierId }, { code: po.supplierSnapshot?.code }] },
    });
    if (!sup) sup = await prisma.supplier.findFirst();
    if (!sup) return;

    let purchaserId = po.purchaserId;
    const pUser = await prisma.user.findUnique({ where: { id: purchaserId } });
    if (!pUser) purchaserId = 'usr-purchaser-01';

    let poRec = await prisma.purchaseOrder.findFirst({
      where: { OR: [{ id: po.id }, { poNumber: po.poNumber }] },
    });

    if (poRec) {
      poRec = await prisma.purchaseOrder.update({
        where: { id: poRec.id },
        data: {
          poNumber: po.poNumber,
          date: new Date(po.date || Date.now()),
          status: po.status as POStatus,
          subtotal: po.subtotal || 0,
          tax: po.tax || 0,
          total: po.total || 0,
          notes: po.notes || null,
          isRevision: po.isRevision || false,
          revisionNotes: po.revisionNotes || null,
          supplierSnapshot: (po.supplierSnapshot as any) || {},
          bankAccountSnapshot: (po.bankAccountSnapshot as any) || {},
        },
      });
    } else {
      poRec = await prisma.purchaseOrder.create({
        data: {
          id: po.id,
          poNumber: po.poNumber,
          date: new Date(po.date || Date.now()),
          supplierId: sup.id,
          purchaserId,
          status: po.status as POStatus,
          subtotal: po.subtotal || 0,
          tax: po.tax || 0,
          total: po.total || 0,
          notes: po.notes || null,
          isRevision: po.isRevision || false,
          revisionNotes: po.revisionNotes || null,
          supplierSnapshot: (po.supplierSnapshot as any) || {},
          bankAccountSnapshot: (po.bankAccountSnapshot as any) || {},
        },
      });
    }

    for (const item of po.items) {
      let uom = await prisma.unitOfMeasure.findFirst({
        where: { OR: [{ id: item.uomId }, { code: item.uomCode }] },
      });
      if (!uom) uom = await prisma.unitOfMeasure.findFirst({ where: { code: 'PCS' } });

      let dbItem = await prisma.item.findFirst({
        where: { OR: [{ id: item.itemId }, { code: item.itemCode }] },
      });
      if (!dbItem) continue;

      let prItemId = item.prItemId || null;
      if (prItemId) {
        const prItemExists = await prisma.purchaseRequestItem.findUnique({ where: { id: prItemId } });
        if (!prItemExists) prItemId = null;
      }

      await prisma.purchaseOrderItem.upsert({
        where: { id: item.id },
        update: {
          orderedQty: item.orderedQty,
          receivedQty: item.receivedQty || 0,
          initialOrderedQty: item.initialOrderedQty || item.orderedQty,
          releasedQty: item.releasedQty || 0,
          releaseReason: item.releaseReason || null,
          unitPrice: item.unitPrice || 0,
          subtotal: item.subtotal || 0,
          notes: item.notes || null,
        },
        create: {
          id: item.id,
          poId: poRec.id,
          prItemId,
          itemId: dbItem.id,
          uomId: uom!.id,
          orderedQty: item.orderedQty,
          receivedQty: item.receivedQty || 0,
          initialOrderedQty: item.initialOrderedQty || item.orderedQty,
          releasedQty: item.releasedQty || 0,
          releaseReason: item.releaseReason || null,
          unitPrice: item.unitPrice || 0,
          subtotal: item.subtotal || 0,
          notes: item.notes || null,
        },
      });
    }
  } catch (err) {
    console.error('[dbBridge] Error syncing PO to MariaDB:', err);
  }
}

export async function syncGRToDatabase(gr: GoodsReceiptRecord) {
  if (process.env.NODE_ENV === 'test' || process.env.SKIP_DB_SYNC === 'true') {
    return;
  }
  try {
    const po = await prisma.purchaseOrder.findFirst({
      where: { OR: [{ id: gr.poId }, { poNumber: gr.poNumber }] },
    });
    if (!po) return;

    let receiverId = gr.receiverId;
    const rUser = await prisma.user.findUnique({ where: { id: receiverId } });
    if (!rUser) receiverId = 'usr-warehouse-01';

    let grRec = await prisma.goodsReceipt.findFirst({
      where: { OR: [{ id: gr.id }, { grNumber: gr.grNumber }] },
    });

    let grStatus: GRStatus = GRStatus.CONFIRMED;
    if (gr.status === 'DRAFT') grStatus = GRStatus.DRAFT;
    else if (gr.status === 'CANCELLED') grStatus = GRStatus.CANCELLED;

    if (grRec) {
      grRec = await prisma.goodsReceipt.update({
        where: { id: grRec.id },
        data: {
          vendorDeliveryNote: gr.vendorDeliveryNote || 'SJ-UNKNOWN',
          receiveDate: new Date(gr.receiveDate || Date.now()),
          status: grStatus,
          notes: gr.notes || null,
        },
      });
    } else {
      grRec = await prisma.goodsReceipt.create({
        data: {
          id: gr.id,
          grNumber: gr.grNumber,
          vendorDeliveryNote: gr.vendorDeliveryNote || 'SJ-UNKNOWN',
          receiveDate: new Date(gr.receiveDate || Date.now()),
          poId: po.id,
          receiverId,
          status: grStatus,
          notes: gr.notes || null,
        },
      });
    }

    for (const it of gr.items) {
      let poItem = await prisma.purchaseOrderItem.findFirst({
        where: { OR: [{ id: it.poItemId }, { poId: po.id, itemId: it.itemId }] },
      });
      if (!poItem) continue;

      let dbItem = await prisma.item.findFirst({
        where: { OR: [{ id: it.itemId }, { code: it.itemCode }] },
      });
      if (!dbItem) continue;

      let uom = await prisma.unitOfMeasure.findFirst({
        where: { OR: [{ id: it.uomId }, { code: it.uomCode }] },
      });
      if (!uom) uom = await prisma.unitOfMeasure.findFirst({ where: { code: 'PCS' } });

      const griRec = await prisma.goodsReceiptItem.upsert({
        where: { id: it.id },
        update: {
          receivedQty: it.receivedQty || 0,
          rejectedQty: it.rejectedQty || 0,
          conditionNotes: it.conditionNotes || null,
        },
        create: {
          id: it.id,
          grId: grRec.id,
          poItemId: poItem.id,
          itemId: dbItem.id,
          uomId: uom!.id,
          receivedQty: it.receivedQty || 0,
          rejectedQty: it.rejectedQty || 0,
          conditionNotes: it.conditionNotes || null,
        },
      });

      if (Number(it.receivedQty) > 0) {
        const costLayerId = it.costLayerId || `layer-${it.id}`;
        const existingLayer = await prisma.inventoryCostLayer.findUnique({ where: { id: costLayerId } });
        if (!existingLayer) {
          await prisma.inventoryCostLayer.create({
            data: {
              id: costLayerId,
              grItemId: griRec.id,
              itemId: dbItem.id,
              initialQty: it.receivedQty,
              remainingQty: it.receivedQty,
              unitCost: it.unitCost || poItem.unitPrice || 0,
              createdAt: new Date(it.createdAt || gr.receiveDate || Date.now()),
            },
          });
        }

        const txId = it.inventoryTxId || `tx-${it.id}`;
        const existingTx = await prisma.inventoryTransaction.findUnique({ where: { id: txId } });
        if (!existingTx) {
          await prisma.inventoryTransaction.create({
            data: {
              id: txId,
              grId: grRec.id,
              itemId: dbItem.id,
              quantity: it.receivedQty,
              txType: InventoryTxType.PURCHASE_RECEIPT,
              txDate: new Date(it.createdAt || gr.receiveDate || Date.now()),
            },
          });
        }
      }
    }

    // Recalculate receivedQty for all PO items and update PO status in MariaDB
    const allDbPoItems = await prisma.purchaseOrderItem.findMany({
      where: { poId: po.id },
    });
    const allGrItems = await prisma.goodsReceiptItem.findMany({
      where: { poItemId: { in: allDbPoItems.map((p) => p.id) } },
    });

    let allComplete = allDbPoItems.length > 0;
    for (const pi of allDbPoItems) {
      const itemsForThisPo = allGrItems.filter((g) => g.poItemId === pi.id);
      const totalRec = itemsForThisPo.reduce((sum: number, gi) => sum + Number(gi.receivedQty), 0);
      await prisma.purchaseOrderItem.update({
        where: { id: pi.id },
        data: { receivedQty: totalRec },
      });
      if (totalRec < Number(pi.orderedQty)) {
        allComplete = false;
      }
    }

    await prisma.purchaseOrder.update({
      where: { id: po.id },
      data: {
        status: allComplete ? POStatus.COMPLETED : POStatus.PARTIALLY_RECEIVED,
      },
    });
  } catch (err) {
    console.error('[dbBridge] Error syncing GR to MariaDB:', err);
  }
}

export async function syncItemToDatabase(item: ItemRecord) {
  if (process.env.NODE_ENV === 'test' || process.env.SKIP_DB_SYNC === 'true') {
    return;
  }
  try {
    let uom = await prisma.unitOfMeasure.findFirst({
      where: { OR: [{ id: item.defaultUomId }, { code: item.uomCode }] },
    });
    if (!uom) uom = await prisma.unitOfMeasure.findFirst({ where: { code: 'PCS' } });
    if (!uom) return;

    await prisma.item.upsert({
      where: { code: item.code },
      update: {
        name: item.name,
        category: item.category,
        specs: item.specs || '',
        defaultUomId: uom.id,
        isActive: item.status !== 'INACTIVE',
      },
      create: {
        id: item.id,
        code: item.code,
        name: item.name,
        category: item.category,
        specs: item.specs || '',
        defaultUomId: uom.id,
        isActive: item.status !== 'INACTIVE',
      },
    });
  } catch (err) {
    console.error('[dbBridge] Error syncing Item to MariaDB:', err);
  }
}

export async function syncSupplierToDatabase(supplier: SupplierRecord) {
  if (process.env.NODE_ENV === 'test' || process.env.SKIP_DB_SYNC === 'true') {
    return;
  }
  try {
    const supRec = await prisma.supplier.upsert({
      where: { code: supplier.code },
      update: {
        name: supplier.name,
        phone: supplier.phone,
        email: supplier.email,
        address: supplier.address,
        paymentTerms: supplier.paymentTerms,
        isActive: supplier.status !== 'INACTIVE',
      },
      create: {
        id: supplier.id,
        code: supplier.code,
        name: supplier.name,
        phone: supplier.phone,
        email: supplier.email,
        address: supplier.address,
        paymentTerms: supplier.paymentTerms,
        isActive: supplier.status !== 'INACTIVE',
      },
    });

    if (Array.isArray(supplier.bankAccounts)) {
      for (const b of supplier.bankAccounts) {
        if (!b.accountNumber || !b.bankName) continue;
        const existing = await prisma.supplierBankAccount.findFirst({
          where: { supplierId: supRec.id, accountNumber: b.accountNumber },
        });
        if (existing) {
          await prisma.supplierBankAccount.update({
            where: { id: existing.id },
            data: { bankName: b.bankName, accountHolderName: b.accountHolderName || supplier.name, isPrimary: b.isPrimary || false },
          });
        } else {
          await prisma.supplierBankAccount.create({
            data: {
              supplierId: supRec.id,
              bankName: b.bankName,
              accountNumber: b.accountNumber,
              accountHolderName: b.accountHolderName || supplier.name,
              isPrimary: b.isPrimary || false,
            },
          });
        }
      }
    }
  } catch (err) {
    console.error('[dbBridge] Error syncing Supplier to MariaDB:', err);
  }
}

export async function syncUomToDatabase(uom: UomRecord) {
  if (process.env.NODE_ENV === 'test' || process.env.SKIP_DB_SYNC === 'true') {
    return;
  }
  try {
    await prisma.unitOfMeasure.upsert({
      where: { code: uom.code },
      update: { name: uom.name, symbol: uom.symbol },
      create: { id: uom.id, code: uom.code, name: uom.name, symbol: uom.symbol },
    });
  } catch (err) {
    console.error('[dbBridge] Error syncing UoM to MariaDB:', err);
  }
}

export async function syncWarehouseToDatabase(warehouse: WarehouseRecord) {
  if (process.env.NODE_ENV === 'test' || process.env.SKIP_DB_SYNC === 'true') {
    return;
  }
  try {
    await prisma.warehouse.upsert({
      where: { code: warehouse.code },
      update: {
        name: warehouse.name,
        type: warehouse.type as any,
        capacity: warehouse.capacity,
        capacityUnit: warehouse.capacityUnit,
        isActive: warehouse.status !== 'INACTIVE',
      },
      create: {
        id: warehouse.id,
        code: warehouse.code,
        name: warehouse.name,
        type: warehouse.type as any,
        capacity: warehouse.capacity,
        capacityUnit: warehouse.capacityUnit,
        isActive: warehouse.status !== 'INACTIVE',
      },
    });
  } catch (err) {
    console.error('[dbBridge] Error syncing Warehouse to MariaDB:', err);
  }
}

export async function syncAuditLogToDatabase(log: PRAuditRecord) {
  if (process.env.NODE_ENV === 'test' || process.env.SKIP_DB_SYNC === 'true') {
    return;
  }
  try {
    let actorId = log.actorId || null;
    if (actorId) {
      const user = await prisma.user.findUnique({ where: { id: actorId } });
      if (!user) actorId = null;
    }

    const logId = log.id || `audit-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    await prisma.auditLog.upsert({
      where: { id: logId },
      update: {},
      create: {
        id: logId,
        entityType: log.entityType || 'PurchaseRequest',
        entityId: log.entityId || 'UNKNOWN',
        fromStatus: log.fromStatus || null,
        toStatus: log.toStatus || null,
        actorId,
        action: log.action || 'ACTION',
        reason: log.reason || null,
        metadata: (log.metadata as any) || {},
        timestamp: new Date(log.timestamp || Date.now()),
      },
    });
  } catch (err) {
    console.error('[dbBridge] Error syncing AuditLog to MariaDB:', err);
  }
}
